// Copyright (c) StrangeBee 2023

import React, { useEffect, ReactElement } from 'react';
import { BrandingParams } from 'thehive-sdk';
import { useLocalMessage } from '@Helpers/locale';
import { UploadImage } from '@Molecules/UploadImage/UploadImage';
import { THForm } from '@Molecules/THForm/THForm';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import useValidateForm from '@Hooks/useValidateForm';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';

import './BrandingForm.scss';

export interface BrandingFormProps {
    onFinish: (data: BrandingParams) => void;
    onRemove: (data: string) => void;
    data: BrandingParams;
}

export const BrandingForm = ({ onFinish, data, onRemove }: BrandingFormProps): ReactElement => {
    const i = useLocalMessage();
    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const onFinishHandler = (data: any) => {
        onFinish(data);
        setFieldsTouched(false);
    };

    useEffect(() => {
        form.resetFields();
    }, [data, form]);

    return (
        <THForm
            name="branding-form"
            form={form}
            initialValues={{ title: 'TheHive', ...data }}
            onFinish={onFinishHandler}
            onValuesChange={() => {
                onFormValuesChange();
                setFieldsTouched(true);
            }}
        >
            <THForm.Layout
                footer={
                    <FormSubmitButton
                        warning
                        onCancel={() => {
                            setFieldsTouched(false);
                            form.resetFields();
                        }}
                        disabled={!isValid}
                        hidden={!fieldsTouched}
                        footerText={i('general.confirm')}
                    />
                }
            >
                <TitleSection>{i('branding.panel.title')}</TitleSection>
                <THForm.Item
                    label={i('account.branding.form.title.label')}
                    rules={[{ required: true, message: i('account.branding.form.title.required') }]}
                    name="title"
                    className="input-half"
                >
                    <THInput />
                </THForm.Item>
                <THForm.Item label={i('account.branding.form.loginLogo')} name="loginLogo">
                    <UploadImage onRemove={onRemove} name="loginLogo" className="branding-logo" />
                </THForm.Item>
                <THForm.Item label={i('account.branding.form.menuLogo')} name="menuLogo">
                    <UploadImage onRemove={onRemove} name="menuLogo" className="branding-menu" />
                </THForm.Item>
                <THForm.Item label={i('account.branding.form.favicon')} name="favicon">
                    <UploadImage onRemove={onRemove} name="favicon" className="branding-icon" />
                </THForm.Item>
            </THForm.Layout>
        </THForm>
    );
};
